// games/sudoku/Sudoku.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaEraser, FaPencilAlt, FaTimes, FaTrophy, FaPlay, FaMagic } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './Sudoku.css';

const Sudoku = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();
  const boardRef = useRef(null);

  const contentMenuTimeRef = useRef(0);

  const [state, setState] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Режимы заметок: постоянный и временный
  const [noteMode, setNoteMode] = useState(false); // Постоянный (кнопка)
  const [tempNoteMode, setTempNoteMode] = useState(false); // Временный (ПКМ / долгий тап)

  // Эффективный режим: включен если работает либо постоянный, либо временный переключатель
  const isEffectiveNoteMode = noteMode || tempNoteMode;

  const [finalData, setFinalData] = useState(null);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (data.type === 'update') {
        setState(data.state);
      }
      // Обрабатываем и finish (победа/смерть) и game_over (таймаут)
      else if (data.type === 'finish' || data.type === 'game_over') {
        // Если прилетел стейт - обновляем (при таймауте может не прилететь, берем старый)
        if (data.state) setState(data.state);

        // Формируем данные для отчета
        // При таймауте data.type = game_over, reason = timeout
        const isTimeout = data.type === 'game_over';

        setFinalData({
            ...data,
            result: isTimeout ? 'lose' : data.result // Таймаут = поражение
        });

        setShowReport(true);

        if (data.result === 'win') window.croco?.haptic?.('success');
        else window.croco?.haptic?.('error');
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, store]);

  // ACTION HANDLERS

  const handleInput = useCallback((num) => {
    if (selectedIdx === null || !state) return;
    const cell = state.grid[selectedIdx];
    if (cell.fixed) return;

    window.croco?.haptic?.('selection');

    // Используем эффективный режим заметок
    if (isEffectiveNoteMode) {
      store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'note',
        index: selectedIdx,
        value: num
      });
    } else {
      store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'input',
        index: selectedIdx,
        value: num
      });
    }
  }, [selectedIdx, state, isEffectiveNoteMode, store]);

  const handleErase = useCallback(() => {
    if (selectedIdx === null || !state) return;
    window.croco?.haptic?.('impactLight');

    // Если режим заметок (любой) - стираем заметки. Иначе - значение.
    if (isEffectiveNoteMode) {
        store.ref.work.methods.hostAction('mafiaMiniGameAction', {
            type: 'clearNotes',
            index: selectedIdx
        });
    } else {
        store.ref.work.methods.hostAction('mafiaMiniGameAction', {
            type: 'input',
            index: selectedIdx,
            value: null
        });
    }
  }, [selectedIdx, state, isEffectiveNoteMode, store]);

  // Обработчик нажатия на магию
  const handleAutoCandidates = useCallback(() => {
    // Вибрация для тактильного отклика
    window.croco?.haptic?.('impactMedium');

    // Отправляем действие на сервер
    store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'autoCandidates'
    });
  }, [store]);

  // Клик по клетке (Левая кнопка / Тап)
  const handleCellClick = (idx) => {

    if (Date.now() - contentMenuTimeRef.current < 300) {
      return;
    }
    if (selectedIdx !== idx) {
        // Переход на новую клетку: всегда сбрасываем временный режим
        setTempNoteMode(false);
    } else {
        // Клик по той же клетке: если был временный режим - выключаем его
        if (tempNoteMode) setTempNoteMode(false);
    }
    setSelectedIdx(idx);
  };

  // Контекстное меню клетки (Правая кнопка / Долгий тап)
  const handleCellContextMenu = (e, idx) => {
    contentMenuTimeRef.current = Date.now();
      e.preventDefault();
      // Выбираем клетку и включаем временный режим
      setSelectedIdx(idx);
      setTempNoteMode(true);
      window.croco?.haptic?.('impactMedium'); // Тактильный отклик для подтверждения смены режима
  };

  // --- KEYBOARD SUPPORT ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!state || showReport) return;
      const size = state.gridSize === '4x4' ? 4 : 9;

      if (e.key >= '1' && e.key <= size.toString()) {
        handleInput(parseInt(e.key));
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        handleErase();
        return;
      }
      if (e.key.toLowerCase() === 'n') {
          // Клавиша N переключает постоянный режим
          setNoteMode(prev => !prev);
          return;
      }
      if (selectedIdx !== null) {
        let newIdx = selectedIdx;
        if (e.key === 'ArrowUp') newIdx -= size;
        if (e.key === 'ArrowDown') newIdx += size;
        if (e.key === 'ArrowLeft') newIdx -= 1;
        if (e.key === 'ArrowRight') newIdx += 1;

        const maxIdx = size * size - 1;
        if (newIdx >= 0 && newIdx <= maxIdx) {
            // При перемещении клавиатурой тоже сбрасываем временный режим, как при клике на новую
            if (newIdx !== selectedIdx) setTempNoteMode(false);
            setSelectedIdx(newIdx);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, selectedIdx, handleInput, handleErase, showReport]);

  // HELPER: Highlight classes
  const getCellClass = (idx, cell) => {
    const size = state.gridSize === '4x4' ? 4 : 9;
    const classes = ['sudoku-cell'];

    if (cell.fixed) classes.push('fixed');
    else if (cell.val !== null) classes.push('user-val');
    if (cell.isError) classes.push('error');
    if (selectedIdx === idx) classes.push('selected');

    if (selectedIdx !== null && selectedIdx !== idx) {
        const selRow = Math.floor(selectedIdx / size);
        const selCol = selectedIdx % size;
        const row = Math.floor(idx / size);
        const col = idx % size;

        const selectedVal = state.grid[selectedIdx].val;
        if (selectedVal !== null && cell.val === selectedVal) {
            classes.push('same-num');
        }
        else if (row === selRow || col === selCol) {
            classes.push('highlighted', boardModeClass);
        }
    }
    return classes.join(' ');
  };

  if (!state) return null;

  const size = state.gridSize === '4x4' ? 4 : 9;
  const numbers = Array.from({ length: size }, (_, i) => i + 1);

  // Класс режима для всего поля (влияет на цвета кнопок и выделения)
  const boardModeClass = isEffectiveNoteMode ? 'mode-notes' : 'mode-input';

  // Определяем класс для кнопки карандаша
  let pencilBtnClass = 'tool-btn';
  if (noteMode) pencilBtnClass += ' note-active'; // Постоянный режим
  else if (tempNoteMode) pencilBtnClass += ' note-temp'; // Временный режим

  // --- REPORT MODAL ---
  const renderReportModal = () => {
    if (!finalData || !showReport) return null;
    // Данные могут прийти либо в `my` (game_over), либо внутри `my` объекта (finish)
    // Упрощаем доступ
    const myStats = finalData.my || {};
    const isWin = finalData.result === 'win';

    return (
      <div className="ms-modal-overlay" onClick={() => setShowReport(false)}>
        <div className="cs-report-card" onClick={e => e.stopPropagation()}>
          <div className="cs-report-header">
             <div className="cs-report-title">
                 {isWin ? t('arcade.sliding_unlocked') : t('arcade.game_over')}
             </div>
          </div>

          <div className="cs-stats-grid">
             <div className="cs-stat-row">
                 <span className="cs-stat-label">{t('arcade.sudoku_score')}</span>
                 <span className="cs-stat-value good">{myStats.score || 0}</span>
             </div>
             <div className="cs-stat-row">
                 <span className="cs-stat-label">{t('arcade.sudoku_mistakes')}</span>
                 <span className="cs-stat-value bad">{myStats.mistakes || 0}</span>
             </div>
             {/* Ранг показываем только если он есть */}
             {myStats.rank && (
                 <div className="cs-stat-row">
                     <span className="cs-stat-label">{t('arcade.wordduel_rank')}</span>
                     <span className="cs-stat-value rank-text">{t(`arcade.${myStats.rank}`)}</span>
                 </div>
             )}
          </div>

          <div style={{margin: '15px 0'}}>
             <UnifiedLeaderboard results={finalData.leaderboard} myId={store.ref.telegram.telegramUserId} t={t} />
          </div>

          {/* Кнопка ЗАКРЫТЬ (Выход) */}
          <button className="cs-close-btn" onClick={onClose}>
             {t('arcade.close')}
          </button>

          {/* Кнопка ПРОДОЛЖИТЬ (Доигрывание) */}
          <button className="cs-close-btn secondary" onClick={() => setShowReport(false)}>
             <FaPlay style={{marginRight: '8px', fontSize: '14px'}}/>
             {t('arcade.stay')}
          </button>

        </div>
      </div>
    );
  };

  return (
    <div
        className={`sudoku-board size-${size} ${boardModeClass}`}
        ref={boardRef}
        tabIndex={0}
        onClick={() => boardRef.current?.focus()}
    >
      {renderReportModal()}

      {/* HEADER */}
      <div className="sudoku-header">
        <div className="sudoku-stat">
          {t('arcade.sudoku_difficulty')}: {t(`arcade.diff_${state.difficulty}`)}
        </div>
        <div className="sudoku-stat mistakes">
           <FaTimes /> {state.mistakes} / {state.maxMistakes}
        </div>
        <div className="sudoku-stat score">
           <FaTrophy /> {state.score}
        </div>
      </div>

      {/* GRID */}
      <div className="sudoku-grid-wrapper">
        <div className={`sudoku-grid size-${size}`}>
           {state.grid.map((cell, idx) => (
             <div
               key={idx}
               className={getCellClass(idx, cell)}
               onClick={() => handleCellClick(idx)}
               onContextMenu={(e) => handleCellContextMenu(e, idx)}
             >
                {cell.val !== null ? (
                    // Если есть основное значение - показываем его
                    <span className="cell-value">{cell.val}</span>
                ) : (
                    // Иначе показываем сетку заметок
                    // grid-2 для 4x4, grid-3 для 9x9
                    <div className={`sudoku-notes grid-${size === 4 ? 2 : 3}`}>
                        {numbers.map(n => (
                            <div key={n} className="note-num">
                                {/* Если заметка есть - выводим цифру, иначе пустоту (для сохранения места в гриде) */}
                                {cell.notes.includes(n) ? n : ''}
                            </div>
                        ))}
                    </div>
                )}
             </div>
           ))}
        </div>
      </div>

      {/* CONTROLS */}
      <div className="sudoku-controls">
         <div className="sudoku-tools">
             <button
                className={pencilBtnClass}
                onClick={() => {
                    // Клик по кнопке переключает постоянный режим и сбрасывает временный
                    setNoteMode(!noteMode);
                    setTempNoteMode(false);
                }}
             >
                <FaPencilAlt />
             </button>

             {/* === КНОПКА МАГИИ (Условный рендеринг) === */}
             {state.autoCandidatesEnabled && (
                 <button className="tool-btn" onClick={handleAutoCandidates}>
                    <FaMagic />
                 </button>
             )}
             {/* ========================================= */}

             <button className="tool-btn" onClick={handleErase}>
                <FaEraser />
             </button>
         </div>

         <div className={`numpad col-${size}`}>
             {numbers.map(num => (
                 <button
                    key={num}
                    className="num-btn"
                    onClick={() => handleInput(num)}
                    // Убрали context menu с самих кнопок, так как логика теперь глобальная через ячейку
                 >
                    {num}
                 </button>
             ))}
         </div>
      </div>
    </div>
  );
};

export default Sudoku;